import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {twMerge} from 'tailwind-merge';
import {Button} from '~/components/Button';
import {Input} from '~/components/Input';
import {TextArea} from '~/components/TextArea';

export type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

interface FormProps {
  onSubmit: (values: ContactFormValues) => void;
  isLoading: boolean;
  error: Error | null;
  data: any | null;
  className?: string;
}

export function Form({onSubmit, isLoading, error, data, className}: FormProps) {
  const {t} = useTranslation();

  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm<ContactFormValues>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset();
    }
  }, [data, reset]);

  return (
    <form
      id="contact-form"
      className={twMerge('flex flex-col', className)}
      method="post"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label={t('nombre') as string}
        error={errors.name}
        id="name"
        className="mb-6"
        placeholder={t('nombre') as string}
        disabled={isLoading}
        {...register('name', {
          required: 'El nombre es requerido.',
        })}
      />

      <Input
        label={t('correo-electronico') as string}
        error={errors.email}
        id="email"
        type="email"
        className="mb-6"
        disabled={isLoading}
        placeholder={t('correo-electronico') as string}
        {...register('email', {
          required: 'El correo electrónico es requerido.',
        })}
      />

      <Input
        label={t('telefono') as string}
        error={errors.phone}
        id="phone"
        type="tel"
        className="mb-6"
        disabled={isLoading}
        placeholder={t('telefono') as string}
        {...register('phone', {
          required: 'El Teléfono es requerido.',
        })}
      />

      <Input
        label={t('asunto') as string}
        error={errors.subject}
        id="subject"
        rows={6}
        className="mb-6"
        disabled={isLoading}
        placeholder={t('asunto') as string}
        {...register('subject', {
          required: 'El asunto es requerido.',
          maxLength: {
            value: 70,
            message: 'Limita el asunto a un máximo de 70 caracteres.',
          },
        })}
      />

      <TextArea
        label={t('mensaje')}
        error={errors.message}
        id="message"
        rows={6}
        className="mb-6"
        disabled={isLoading}
        placeholder={t('pages:contacto:form.message.placeholder') as string}
        {...register('message', {
          required: 'El mensaje es requerido.',
        })}
      />

      {error ? (
        <p className="mb-6 font-medium text-red-600">{error.message}</p>
      ) : null}

      {data ? (
        <p className="mb-6 font-medium text-green-600">
          <Trans i18nKey="pages:contacto:success.message" />
        </p>
      ) : null}

      <Button
        type="submit"
        className="self-start w-[250px]"
        isLoading={isLoading}
      >
        <Trans i18nKey="enviar" />
      </Button>
    </form>
  );
}
